import {
  IconBox,
  IconBuildingStore,
  IconChartArrowsVertical,
  IconChartBar,
  IconCreditCard,
  IconReceipt,
} from '@tabler/icons-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Leaderboard,
  MasterStackBarChart,
  ScalrCarousel,
  TreeTable,
} from '@/components/customComponents'
import { createTab, TabConfig } from '@/components/TabSkeleton'
import { DateRangeValidator } from '@/components/ui/charts/DateRangeValidator'
import { MasterChart } from '@/components/ui/charts/MasterChart'
import { Tabs, TabsList } from '@/components/ui/tabs'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

import { chartColors } from '@/constants/COLOR_PALETTE'
import { usePnLMetrics, usePnLStackChartMetrics } from '@/hooks'
import usePnlStoreHealthCheck from '@/hooks/clarity/usePnlStoreHealthCheck'
import { TranslationFunctionType } from '@/i18n'
import {
  fetchPnlRegions,
  selectPageRefreshTrigger,
  selectPnlAllBranchNames,
  selectPnlRegionsCountry,
  useAppDispatch,
  useAppSelector,
  useReduxBaseFilter,
} from '@/store'
import { CountryFilterType } from '@/store/filterSlice'
import { dateToString } from '@/utils'

const MINI_METRIC_CONFIGS = (t: TranslationFunctionType) => [
  {
    key: 'storeProfits' as const,
    header: t('clarity.pnl.storeProfits'),
    icon: (
      <IconCreditCard className="w-6 h-6" style={{ color: chartColors[0] }} />
    ),
  },
  {
    key: 'netSales' as const,
    header: t('clarity.pnl.netSales'),
    icon: (
      <IconChartArrowsVertical
        className="w-6 h-6"
        style={{ color: chartColors[1] }}
      />
    ),
  },
  {
    key: 'cogs' as const,
    header: t('clarity.metrics.cogs'),
    icon: <IconBox className="w-6 h-6" style={{ color: chartColors[2] }} />,
    isIncreasingPositive: false,
  },
  {
    key: 'storeLabor' as const,
    header: t('clarity.pnl.storeLabor'),
    icon: (
      <IconChartBar className="w-6 h-6" style={{ color: chartColors[3] }} />
    ),
    isIncreasingPositive: false,
  },
  {
    key: 'opEx' as const,
    header: t('clarity.pnl.opEx'),
    icon: <IconReceipt className="w-6 h-6" style={{ color: chartColors[4] }} />,
    isIncreasingPositive: false,
  },
  {
    key: 'rentAndOccupancy' as const,
    header: t('clarity.pnl.rentAndOccupancy'),
    icon: (
      <IconBuildingStore
        className="w-6 h-6"
        style={{ color: chartColors[5] }}
      />
    ),
    isIncreasingPositive: false,
  },
]

const CHART_DATA_CONFIGS = (t: TranslationFunctionType) => [
  {
    key: 'netSales' as const,
    header: t('clarity.pnl.netSales'),
    icon: (
      <IconChartArrowsVertical
        className="w-6 h-6"
        style={{ color: chartColors[1] }}
      />
    ),
    tooltipDisplayType: 'money' as const,
  },
  {
    key: 'cogs' as const,
    header: t('clarity.metrics.cogs'),
    icon: <IconBox className="w-6 h-6" style={{ color: chartColors[2] }} />,
  },
  {
    key: 'storeLabor' as const,
    header: t('clarity.pnl.storeLabor'),
    icon: (
      <IconChartBar className="w-6 h-6" style={{ color: chartColors[3] }} />
    ),
  },
  {
    key: 'opEx' as const,
    header: t('clarity.pnl.opEx'),
    icon: <IconReceipt className="w-6 h-6" style={{ color: chartColors[4] }} />,
  },
  {
    key: 'rentAndOccupancy' as const,
    header: t('clarity.pnl.rentAndOccupancy'),
    icon: (
      <IconBuildingStore
        className="w-6 h-6"
        style={{ color: chartColors[5] }}
      />
    ),
  },
]

export default function ProfitAndLossPage() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const {
    selectedBranch,
    date,
    dateFilterType,
    countryFilter,
    pnlBranch,
    setPnlBranch,
  } = useReduxBaseFilter('profit-and-loss')
  const refreshTrigger = useAppSelector(
    selectPageRefreshTrigger('profit-and-loss')
  )
  const pnlRegionsCountry = useAppSelector(selectPnlRegionsCountry)
  const pnlAllBranchNames = useAppSelector(selectPnlAllBranchNames)

  const [executeStackChartMetrics, setExecuteStackChartMetrics] =
    useState(false)

  // Fetch UK (or non-US) regions on mount when country is defaulted to UK so hooks get correct branches
  useEffect(() => {
    if (countryFilter && countryFilter !== 'US') {
      dispatch(fetchPnlRegions(countryFilter))
    }
  }, [countryFilter, dispatch])

  // When PnL regions have loaded for current country and pnlBranch is not set, set default to all branches
  useEffect(() => {
    if (
      countryFilter &&
      countryFilter !== 'US' &&
      pnlRegionsCountry === countryFilter &&
      pnlAllBranchNames &&
      pnlBranch === null
    ) {
      setPnlBranch(pnlAllBranchNames, 'all')
    }
  }, [
    countryFilter,
    pnlRegionsCountry,
    pnlAllBranchNames,
    pnlBranch,
    setPnlBranch,
  ])

  useEffect(() => {
    const timer = setTimeout(() => {
      setExecuteStackChartMetrics(true)
    }, 1500)

    return () => clearTimeout(timer)
  }, [])

  // Use PnL-specific branches when country is non-default (e.g. GBP -> UK branches);
  // use '' until UK branches are loaded so hooks don't run with wrong (US) selectedBranch
  const effectiveBranch =
    countryFilter && countryFilter !== 'US' ? (pnlBranch ?? '') : selectedBranch

  const {
    healthData,
    storeHealthCheckColumns,
    storeHealthCheckColumnsNetSales,
    loading: storeHealthCheckLoading,
    refetch: storeHealthCheckRefetch,
  } = usePnlStoreHealthCheck(
    dateToString(date, 'from', 'yyyy-mm'),
    dateToString(date, 'to', 'yyyy-mm'),
    countryFilter
  )

  const {
    hierarchyData,
    metricCardData,
    metricCardTrendData,
    pnlTableColumns,
    loading,
    refetch: pnlRefetch,
  } = usePnLMetrics(
    dateToString(date, 'from', 'yyyy-mm'),
    dateToString(date, 'to', 'yyyy-mm'),
    effectiveBranch
  )

  const {
    metricCardRecord,
    loading: stackChartLoading,
    refetch: stackChartRefetch,
  } = usePnLStackChartMetrics(
    executeStackChartMetrics ? effectiveBranch : '',
    executeStackChartMetrics ? date : undefined
  )

  const getHierarchyDataByTopLevel = (id: string) => {
    return hierarchyData?.find((item) => item.id === id)?.children || []
  }

  const { tabs } = useMemo(() => {
    const configs: TabConfig[] = [
      {
        value: 'sales',
        title: t('clarity.pnl.sales'),
        content: (
          <PnLTable
            tableData={getHierarchyDataByTopLevel('super_sales')}
            pnlTableColumns={pnlTableColumns}
            loading={loading}
            country={countryFilter}
          />
        ),
      },
      {
        value: 'cogs',
        title: t('clarity.metrics.cogs'),
        content: (
          <PnLTable
            tableData={getHierarchyDataByTopLevel('super_cogs')}
            pnlTableColumns={pnlTableColumns}
            loading={loading}
            country={countryFilter}
            isIncreasingPositive={false}
          />
        ),
      },
      {
        value: 'storeLabor',
        title: t('clarity.pnl.storeLabor'),
        content: (
          <PnLTable
            tableData={getHierarchyDataByTopLevel('super_labor')}
            pnlTableColumns={pnlTableColumns}
            loading={loading}
            country={countryFilter}
            isIncreasingPositive={false}
          />
        ),
      },
      {
        value: 'opEx',
        title: t('clarity.pnl.opEx'),
        content: (
          <PnLTable
            tableData={getHierarchyDataByTopLevel('super_opex')}
            pnlTableColumns={pnlTableColumns}
            loading={loading}
            country={countryFilter}
            isIncreasingPositive={false}
          />
        ),
      },
      {
        value: 'rentAndOccupancy',
        title: t('clarity.pnl.rentAndOccupancy'),
        content: (
          <PnLTable
            tableData={getHierarchyDataByTopLevel('super_rent_&_occupancy')}
            pnlTableColumns={pnlTableColumns}
            loading={loading}
            country={countryFilter}
            isIncreasingPositive={false}
          />
        ),
      },
      {
        value: 'aboveUnitAG',
        title: t('clarity.pnl.storeEbitda'),
        content: (
          <PnLTable
            tableData={getHierarchyDataByTopLevel('super_above_unit')}
            pnlTableColumns={pnlTableColumns}
            loading={loading}
            country={countryFilter}
          />
        ),
      },
    ]

    return {
      tabs: configs.map((config) => createTab(config)),
    }
  }, [hierarchyData, pnlTableColumns, loading, countryFilter])

  // Refetch data when refresh trigger is triggered
  useEffect(() => {
    if (refreshTrigger) {
      console.log('Refreshing profit and loss page data...')
      pnlRefetch()
      storeHealthCheckRefetch()
      stackChartRefetch()
    }
  }, [refreshTrigger, pnlRefetch, storeHealthCheckRefetch, stackChartRefetch])

  const getTruncatedBranchNames = (branchNames: string) => {
    const branches = branchNames.split(',').map((name) => name.trim())
    const fullText = branches.join(', ')

    if (branches.length <= 2) {
      return { displayText: fullText, fullText, shouldTruncate: false }
    }

    const firstBranch = branches[0]
    const secondBranch = branches[1]

    const displayText = `${firstBranch}, ${secondBranch}${branches.length > 2 ? ', ...' : ''}`

    return { displayText, fullText, shouldTruncate: true }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3 mb-10">
        <h2 className="text-2xl tracking-tight">
          {t('clarity.pnl.topMovers')}
        </h2>
        <ScalrCarousel>
          <Leaderboard
            data={healthData?.netSales}
            title={t('clarity.pnl.netSales')}
            columns={storeHealthCheckColumnsNetSales}
            loading={storeHealthCheckLoading}
            sortBy="percentChange"
            sortOrder="asc"
            enablePagination
            country={countryFilter}
            showBadge={false}
          />
          <Leaderboard
            data={healthData?.cogs}
            title={t('clarity.metrics.cogs')}
            columns={storeHealthCheckColumns}
            loading={storeHealthCheckLoading}
            sortBy="impact"
            enablePagination
            country={countryFilter}
            showBadge={false}
          />
          <Leaderboard
            data={healthData?.storeLabor}
            title={t('clarity.pnl.storeLabor')}
            columns={storeHealthCheckColumns}
            loading={storeHealthCheckLoading}
            sortBy="impact"
            enablePagination
            country={countryFilter}
            showBadge={false}
          />
          <Leaderboard
            data={healthData?.opEx}
            title={t('clarity.pnl.opEx')}
            columns={storeHealthCheckColumns}
            loading={storeHealthCheckLoading}
            sortBy="impact"
            enablePagination
            country={countryFilter}
            showBadge={false}
          />
          <Leaderboard
            data={healthData?.ebitda}
            title={t('clarity.pnl.ebitda')}
            columns={storeHealthCheckColumns}
            loading={storeHealthCheckLoading}
            sortBy="percentChange"
            sortOrder="asc"
            enablePagination
            country={countryFilter}
            showBadge={false}
          />
        </ScalrCarousel>
      </div>

      <MasterStackBarChart
        title={t('clarity.pnl.trendChart')}
        metricCardRecord={metricCardRecord}
        miniCardData={metricCardData}
        trendData={metricCardTrendData}
        loading={stackChartLoading}
        miniMetricConfigs={MINI_METRIC_CONFIGS(t)}
        chartDataConfigs={CHART_DATA_CONFIGS(t)}
        country={countryFilter}
      />

      <div>
        <div className="flex items-center gap-2 mb-4">
          {(() => {
            const { displayText, fullText, shouldTruncate } =
              getTruncatedBranchNames(effectiveBranch)

            if (!shouldTruncate) {
              return <h3 className="text-2xl tracking-tight">{displayText}</h3>
            }

            return (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <h3 className="text-2xl tracking-tight cursor-help">
                      {displayText}
                    </h3>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">{fullText}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )
          })()}
        </div>
        <Tabs defaultValue="sales" className="w-full">
          <TabsList className="w-full">
            {tabs.map((tab) => tab.trigger)}
          </TabsList>
          {tabs.map((tab) => tab.content)}
        </Tabs>
      </div>

      <DateRangeValidator
        dateRange={date}
        chartType="profit & loss chart"
        chartTitle={t('clarity.charts.profitAndLossChart')}
      >
        <MasterChart
          chartType="pnl"
          dateRange={date}
          selectedBranchFilter={effectiveBranch}
          chartTitle={t('clarity.charts.profitAndLossChart')}
          country={countryFilter}
        />
      </DateRangeValidator>
    </div>
  )
}

function PnLTable({
  tableData,
  pnlTableColumns,
  loading,
  country,
  isIncreasingPositive,
}: {
  tableData: any[]
  pnlTableColumns: any[]
  loading: boolean
  country?: CountryFilterType
  isIncreasingPositive?: boolean
}) {
  return (
    <TreeTable
      data={tableData}
      columns={pnlTableColumns}
      isCard={false}
      loading={loading}
      fitContent
      country={country}
      isNormal={isIncreasingPositive}
    />
  )
}
